<template>
  <div class="ccrs-root">
    <div
      id="CCRSPage"
      class="ccrs"
    >
      <div class="ccrs__title">
        <h1 class="ccrs__title__h1 h1--underline">
          {{ i18n.tv('ccrsPageHeader', 'ccrs') }}
        </h1>
        <h2 class="ccrs__title__h2">
          {{ i18n.tv('findTheRightProduct', 'ccrs') }}
        </h2>
      </div>
      <div class="ccrs__main-controls">
        <div class="ccrs__search-wrapper">
          <label
            for="ccrs-search-input"
            class="ccrs__search-label label"
          >
            {{ i18n.tv('ccrsSearchInputLabel', 'ccrs') }}
          </label>
          <input
            id="ccrs-search-input"
            v-model="searchQuery"
            type="text"
            autocomplete="off"
            class="ccrs__search-input"
            :placeholder="i18n.tv('ccrsSearchInputPlaceholder', 'ccrs')"
            aria-labelledby="ccrs-search-input"
            @focus="onInputFocus"
            @blur="handleBlur"
            @input="onSearchInput"
            @keydown.enter="handleInputEnterKey"
          >
          <a
            v-show="searchQuery.length > 0"
            class="ccrs__search-clear"
            href="javascript:void(0)"
            :title="i18n.tv('ClearSearch', 'searchResults')"
            tabindex="-1"
            @click="handleClearSearch"
          >
            <NavigationClose />
          </a>
          <CcrsDropdownSkeleton v-show="isOpen && isLoading" />
          <div
            v-if="isOpen && !isLoading && flattenedResults.length > 0"
            ref="dropdownRef"
            class="ccrs__dropdown"
            @focusin="handleDropdownFocusIn"
            @focusout="handleDropdownFocusOut"
            @scroll="handleDropdownScroll"
            @keydown.enter="handleDropdownEnterKey"
            @touchstart="handleTouchStart"
            @touchend="handleTouchEnd"
            @touchmove="handleTouchMove"
          >
            <div
              v-if="itemNumbers.length > 0 || designs.length > 0"
              class="dropdown__section"
              :class="{ 'dropdown__section--opened': itemsOpened }"
            >
              <div
                class="dropdown__info-header"
                v-html="i18n.tv({messageKey: 'ccrsSearchResultsInfo', messageValues: {itemNumbersCount: productCount, designsCount: designCount}}, 'ccrs')"
              />
              <div
                v-if="itemNumbers.length > 0"
                class="section__header"
                @mousedown.prevent
                @click.stop="itemsOpened = !itemsOpened"
              >
                <strong v-html="i18n.tv({messageKey: 'ccrsItemNumbersHeader', messageValues: {count: productCount}}, 'ccrs')" />
                <NavigationExpandMore
                  class="navigation_expand_more"
                />
              </div>
              <div
                v-if="itemNumbers.length > 0"
                class="section__items"
              >
                <div
                  v-for="item in itemNumbers"
                  :key="item.id"
                  :tabindex="0"
                  class="item"
                  @click="selectItem(item)"
                  @keydown.enter="selectItem(item)"
                  @keydown.space.prevent="selectItem(item)"
                >
                  <div class="item__line">
                    <span
                      class="item__value"
                      v-html="item.vendorName"
                    />,&nbsp;
                    <span
                      class="item__value"
                      v-html="item.itemNumber"
                    />
                  </div>
                  <ul class="item__list">
                    <li class="item__line">
                      <span class="item__value">
                        <span v-html="item.designCategoryName" /><template v-if="item.designCategoryName && item.title">, </template><span v-html="item.title" /><template v-if="(item.designCategoryName || item.title) && item.material">, </template><span>{{ item.material }}</span>
                      </span>
                    </li>
                    <li
                      v-if="item?.attributes && item.attributes.length > 0"
                      class="item__line"
                    >
                      <span class="item__label">{{ i18n.tv('ccrsMeasurementsLabel', 'ccrs') }} [{{ currentUnitSystem }}]:</span>
                      <span class="item__value">
                        <span
                          v-for="(attr, index) in item.attributes"
                          :key="attr.productAttributeId"
                        >
                          {{ i18n.tv(attr.technicalName, 'attributes') }} {{ attr.value }}
                          <template v-if="index < item.attributes.length - 1">; </template>
                        </span>
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <CcrsLoadingMoreSkeleton
                v-if="isLoadingMore"
                variant="product"
                :count="20"
              />
            </div>
            <div
              v-if="designs.length > 0"
              ref="designsSectionRef"
              class="dropdown__section"
              :class="{ 'dropdown__section--opened': designsOpened }"
            >
              <div
                class="section__header dropdown__section--designs"
                @mousedown.prevent
                @click.stop="designsOpened = !designsOpened"
              >
                <strong v-html="i18n.tv({messageKey: 'ccrsDesignsHeader', messageValues: {count: designCount}}, 'ccrs')" />
                <NavigationExpandMore
                  class="navigation_expand_more"
                />
              </div>
              <div class="section__items">
                <div
                  v-for="design in designs"
                  :key="design.id"
                  :tabindex="0"
                  class="item design"
                  @click="selectItem(design)"
                  @keydown.enter="selectItem(design)"
                  @keydown.space.prevent="selectItem(design)"
                >
                  <div class="item__title">
                    <span
                      v-if="design.vendorName"
                      class="item__value"
                      v-html="design.vendorName"
                    />
                    <template v-if="design.vendorName && design.title">
                      {{ ', ' }}
                    </template>
                    <span
                      v-if="design.title"
                      class="item__value"
                      v-html="design.title"
                    />
                  </div>
                  <div class="item__description">
                    <span
                      v-if="design.category"
                      class="item__value"
                      v-html="design.category"
                    />
                  </div>
                </div>
              </div>
              <CcrsLoadingMoreSkeleton
                v-if="isLoadingMoreDesigns"
                variant="design"
                :count="10"
              />
            </div>
          </div>
          <div
            v-show="isOpen && !isLoading && searchQuery.length > 0 && flattenedResults.length === 0 && hasSearched"
            class="ccrs__dropdown"
          >
            <div class="ccrs__no-results">
              <div>{{ i18n.tv('ccrsNoResultsFound', 'ccrs') }}</div>
              <a
                class="ccrs__open-contact"
                href="javascript:void(0)"
                :title="i18n.tv('showContactDetails', 'ccrs')"
                @click="openContactDetails(false)"
              >
                {{ i18n.tv('showContactDetails', 'ccrs') }}
              </a>
            </div>
          </div>
        </div>
        <div class="ccrs__main-controls__vendor">
          <label
            for="competitor-autocomplete"
            class="ccrs__search-label"
          >
            {{ i18n.tv('ccrsVendorLabel', 'ccrs') }}
          </label>
          <ComboDropDown
            id="competitor-autocomplete"
            ref="competitorCombo"
            v-model="selectedCompetitorId"
            :data-items="competitorOptions"
            text-field="name"
            data-item-key="id"
            :filterable="true"
            :clear-button="true"
            class="ccrs__main-controls__vendor__autocomplete ccrs__autocomplete--with-clear"
            :placeholder="i18n.tv('ccrsVendorPlaceholder', 'ccrs')"
          />
        </div>
      </div>
    </div>
    <div class="ccrs__results-wrapper">
      <div
        v-if="selectedTitle"
        class="ccrs__results-wrapper__results"
      >
        <h2
          v-if="(products?.count > 0 && !showLoadingSkeleton)"
        >
          {{ i18n.tv('results', 'ccrs') }}
        </h2>
        <result-expandable
          v-if="((ccrsSearchParameters !== null && (products?.count > 0 && !showLoadingSkeleton)) || (ccrsSearchParameters !== null && !showLoadingSkeleton)) || !filtersDisabled"
          v-model="resultsExpanded"
          :title="selectedTitle"
          :info="!resultsExpanded ? displayInfo : ''"
          :vendor="ccrsSearchParameters?.competitor ?? undefined"
          :category="i18n.tv(ccrsSearchParameters?.category, 'categories')"
          :design="ccrsSearchParameters?.design ?? undefined"
          :compound="ccrsSearchParameters?.compound ?? undefined"
          :type="selectedItemType"
          :item-number="ccrsSearchParameters?.articleNo"
          :filters="initialCcrsFilters ?? undefined"
        />
        <ResultsSkeleton
          v-else-if="showLoadingSkeleton || (showLoadingSkeleton && filtersDisabled) || filtersDisabled"
        />
        <div
          class="ccrs__results__filters"
        >
          <CategoryFilter
            v-if="((categoryFilters?.length && (products?.count > 0 && !showLoadingSkeleton)) || (categoryFilters?.length && !showLoadingSkeleton)) || !filtersDisabled"
            ref="filterRef"
            :filters="filters"
            :facets="facets"
            :product-count="products?.length"
            :show-chips="false"
            :show-reset-on-ccrs="true"
            :disable-buttons="filtersDisabled"
            :disable-reset="resetDisabled"
            @applyFilters="handleApplyFilter"
            @resetFilters="handleResetFilters"
            @filterChanged="handleFilterChanged"
          />
          <FilterSkeleton
            v-else-if="(showLoadingSkeleton && !products?.count) || (showLoadingSkeleton && filtersDisabled) || filtersDisabled"
            :no-padding="true"
          />
        </div>
        <div class="ccrs__results__filters">
          <HtmlMessageContainer
            v-if="!productsLoading && filtersAdjustedForZeroProducts && !showLoadingSkeleton"
            :is-info="true"
            class="ccrs__zero-products-message"
          >
            <div class="html-message__content">
              <span>{{ i18n.tv('ccrsZeroProductsFilterAdjusted', 'ccrs') }}</span>
              <a
                class="ccrs__open-contact"
                href="javascript:void(0)"
                @click="openContactDetails(true)"
              >
                {{ i18n.tv('showContactDetails', 'ccrs') }}
              </a>
            </div>
          </HtmlMessageContainer>
          <NoProductsFound
            v-show="!showLoadingSkeleton && (!products?.count || products?.count === 0)"
            :products="products?.count !== undefined ? products : {count: 0}"
            :has-active-values="shouldShowResetFilters"
            :form-data="formData"
            :reset-in-progress="resetInProgress"
            :search-query="searchQuery"
            :use-popup="true"
            :ccrs-type="selectedItemType"
            @reset-filters="handleResetFilters"
            @clear-form-data="clearFormData"
          />
          <ProductTableSkeleton
            v-if="showLoadingSkeleton"
            :is-ccrs="true"
          />
          <ProductTable
            v-else-if="columnsFromFilters && products?.count > 0 && !showLoadingSkeleton"
            ref="productTableRef"
            :is-loading="productsLoading"
            :columns="columnsFromFilters"
            :products="getMappedProducts(products)"
            :sort="tableOptions.sort"
            :pagination="tableOptions.pagination"
            :total="products?.count"
            :prices="prices"
            :loading-indicators="loadingIndicators"
            :availabilities="availabilities"
            :successor-availabilities="successorAvailabilities"
            :show-design-sketch="true"
            :is-default-customer="isDefaultCustomer"
            :is-ccrs="true"
            :ccrs-type="selectedItemType"
            :open-detail-in-new-tab="true"
            @shopItem="addItemToBasket"
            @sortByColumn="sortItems"
            @changePage="changePageLocal"
            @filterInchValues="loadProducts"
            @calculate="handleCalculate"
          />
          <HtmlMessageContainer
            v-show="!productsLoading && products?.count > 0 && !showLoadingSkeleton"
            :is-info="true"
          >
            <div class="html-message__content">
              <span>{{ i18n.tv('ccrsSuitability', 'ccrs') }}</span>
              <span>{{ i18n.tv('productDataSheet', 'ccrs') }}</span>
            </div>
          </HtmlMessageContainer>
        </div>
      </div>
    </div>
    <PopupBase
      :show="openCartModal"
      @close-pop-up="closeCartModal"
      css-class="popup__buy-box-new"
    >
      <ProductBuyBox
        :product-data="currentProduct"
        :product="currentProduct"
        :is-default-customer="true"
        :loading="reloadProductsInProgress"
        class="buy-box-in-popup"
        @productSuccessfullyAdded="onProductSuccessfullyAdded"
        @reloadProduct="reloadProduct"
      />
    </PopupBase>
    <ContactDetailsPopup
      :is-open="isContactPopupOpen"
      :search-term="searchQuery"
      :additional-info="contactAdditionalInfo"
      :tracking-callback="trackContactSend"
      :track-copy-mail="trackCopyMail"
      :track-click-mail="trackClickMail"
      :competitor-name="selectedCompetitorId?.name || ''"
      @close="closeContactPopup"
    />
    <PopupCcrsDisclaimer
      :show="loadingDone && showDisclaimer"
      :class="loadingDone && showDisclaimer ? 'popup--visible' : ''"
    />
  </div>
</template>

<script setup lang="ts">
import {ref, computed, onMounted, watch, nextTick} from 'vue'
import type {DetailsRequestBody, ApiFilter} from '~/types/ccrs'
import useCcrsService from '~/composables/ccrs_service'
import useI18nExtended from '~/composables/usei18nExtended'
import {useCcrsSearch} from '~/composables/useCcrsSearch'
import {useCcrsDropdown} from '~/composables/useCcrsDropdown'
import {useCcrsFilterHandlers} from '~/composables/useCcrsFilterHandlers'
import {useCcrsTracking} from '~/composables/useCcrsTracking'
import {computeInitialCcrsFilterValues} from '~/composables/useInitialCcrsFilterValues'
import NavigationClose from '~/assets/icons/navigation_close.svg?component'
import ComboDropDown from '~/components/input-components/ComboDropDown.vue'
import ContactDetailsPopup from '~/components/ccrs/ContactDetailsPopup.vue'
import CcrsDropdownSkeleton from '~/components/ccrs/CcrsDropdownSkeleton.vue'
import CcrsLoadingMoreSkeleton from '~/components/ccrs/CcrsLoadingMoreSkeleton.vue'
import CategoryFilter from '~/components/category/CategoryFilter'
import PopupCcrsDisclaimer from '~/components/popup/PopupCcrsDisclaimer.vue'
import NoProductsFound from '~/components/category/NoProductsFound.vue'
import ResultExpandable from '~/components/ccrs-new/ResultExpandable.vue'
import ProductTable from '~/components/product-detail/ProductTable'
import ProductTableSkeleton from '~/components/skeletons/ProductTableSkeleton.vue'
import PopupBase from '~/components/popup/PopupBase'
import FilterSkeleton from '~/components/skeletons/FilterSkeleton.vue'
import ResultsSkeleton from '~/components/skeletons/ResultsSkeleton.vue'
import ProductBuyBox from '~/components/product-detail/ProductBuyBox.vue'
import HtmlMessageContainer from '~/components/HtmlMessageContainer.vue'
import {usePimHelper} from '~/composables/pim_helper'
import {cleanupFacets, cleanupFilters, SHOW_INCH_VALUES_FILTER} from '~/assets/js/filters'
import {useCategoryFilters} from '~/composables/category_filters'
import {NON_ATTRIBUTE_FILTERS, PROPERTY_SCOPE_NAMES, FACET_VALUE_TYPES, FORM_DEFAULT_DATA} from '~/assets/js/constants'
import {useUserStore} from '@/store/user'
import {useProductAvailabilityPricesHelper} from '~/composables/product_availabilities_prices_helper'
import {getMappedProducts, getMappedProductNew} from '~/assets/js/utils/product_attributes_mapping'
import {getSelectByScope} from '~/composables/product_service_helper'
import {useReloadProduct} from '~/composables/reload_product'
import {useDefaultCustomer} from '~/composables/default_customer'
import {useConfigurationDataStore} from '@/store/configuration-data'
import useBreadcrumb from '~/composables/useBreadcrumb'
import createSeoMetaInfo from '~/assets/js/opengraph/helper'
import NavigationExpandMore from '~/assets/icons/navigation_expand_more.svg?component'
import {useDynamicEvents} from '~/composables/useDynamicEvents'

// Core setup
const nuxtApp = useNuxtApp()
const i18n = useI18nExtended()
const route = useRoute()
const router = useRouter()
const auth = nuxtApp.$auth
const {loadedCustomerUuid, showInchValues} = storeToRefs(useUserStore())
const {buildDynamicProductProps} = useDynamicEvents()

// Page Head
const BASE_URL = useRuntimeConfig().public.BASE_URL
const region = nuxtApp.$globalization.getRegion()
const $md = 768

const seoInfo = createSeoMetaInfo({
  title: i18n.tv('ogTitle', 'ccrs'),
  description: i18n.tv('ogDescription', 'ccrs'),
  image: BASE_URL + '/img/ccrs_og_image.jpg',
  url: `${BASE_URL}/${region.toLowerCase()}/${i18n.locale.value}/cross-reference`,
  locale: i18n.locale.value
})

useSeoMeta(seoInfo)

const i18nHead = useLocaleHead({addSeoAttributes: true, route, router, i18n}).value
useHead({...i18nHead, title: i18n.tv('title', 'ccrs')})
useBreadcrumb([{label: i18n.tv('title', 'ccrs')}])

// Services
const {postSuggestions, getCompetitors, postDetails} = useCcrsService()
const {mapCategoryFilter, mapCcrsTableColumnsFromFilters} = usePimHelper()

// Tracking
const {
  trackSearchEvent, trackResultClick, trackVendorSelection, trackResetToSearch,
  trackApplyFilter, trackAddToCart, trackShowContactDetails, trackContactSend,
  trackCopyMail, trackClickMail
} = useCcrsTracking()

// Competitor selection
type CompetitorOption = {id: number; name: string}
const competitorOptions = ref<CompetitorOption[]>([])
const topCompetitorOptions = ref<CompetitorOption[]>([
  {id: 4, name: 'Angst + Pfister'},
  {id: 5, name: 'anyseals'},
  {id: 3, name: 'Parker'},
  {id: 2, name: 'SKF'},
  {id: 1, name: 'Trelleborg'}
])
const selectedCompetitorId = ref<CompetitorOption | null>(null)
const competitorCombo = ref<InstanceType<typeof ComboDropDown> | null>(null)
const productPropsKeys = ['sku', 'brandName', 'deliveryPlantCode', 'status', 'categories', 'countryOfOrigin']

const {
  searchQuery, isLoading, hasSearched, isLoadingMore, isLoadingMoreDesigns,
  itemNumbers, designs, productCount, designCount, flattenedResults,
  triggerSearch, performSearch, loadMoreProducts, loadMoreDesigns,
  clearSearch, resetPagination, hasResultsForCurrentTerm
} = useCcrsSearch({
  postSuggestions,
  selectedCompetitorId,
  onSearchComplete: trackSearchEvent,
  locale: i18n.locale.value
})

// Types
interface ProductsResponse {
  count: number
  products?: any[]
  searchResults?: any[]
  facets?: Record<string, any>
  [key: string]: any
}

// State
const loadingDone = ref(false)
const selectedTitle = ref<string | undefined>(undefined)
const selectedItemType = ref<'product' | 'design'>('design')
const selectedId = ref<string | number | null>(null)
const products = ref<ProductsResponse | undefined>(undefined)
const categoryFilters = ref<any[] | undefined>([])
const defaultFilters = ref<any[] | undefined>([])
const defaultFiltersInitialized = ref(false)
const facets = ref<Record<string, Array<{value: string; count: number}>> | undefined>()
const initialCcrsFilters = ref<any[] | null>(null)
const ccrsSearchParameters = ref<null | {competitor?: string | null; category?: string | null; design?: string | null; articleNo?: string | null; compound?: string | null}>(null)
const resultsExpanded = ref(false)
const firstLoad = ref(true)
const productsLoading = ref(false)
const openCartModal = ref(false)
const isContactPopupOpen = ref(false)
const filtersDisabled = ref(true)
const resetDisabled = ref(true)
const userHasModifiedFilters = ref(false)
const filtersAdjustedForZeroProducts = ref(false)
const itemsOpened = ref<boolean>(true)
const designsOpened = ref<boolean>(true)
const contactAdditionalInfo = ref<string | undefined>(undefined)

// Computed values
const initialCcrsFilterValues = computed(computeInitialCcrsFilterValues(initialCcrsFilters, showInchValues, i18n))
const showDisclaimer = computed(() => !useConfigurationDataStore().acceptedCcrsDisclaimer)
const currentUnitSystem = computed(() => showInchValues.value ? i18n.tv('inch', 'unitsOfMeasure') : i18n.tv('mm', 'unitsOfMeasure'))

const displayInfo = computed(() => {
  const resultInfo = [ccrsSearchParameters.value?.competitor, ccrsSearchParameters.value?.category ? i18n.tv(ccrsSearchParameters.value.category, 'categories') : null, ccrsSearchParameters.value?.design].filter(Boolean).join(' , ')
  const parts = [resultInfo, ccrsSearchParameters.value?.articleNo].filter(Boolean)
  if (selectedItemType.value === 'product') {
    parts.push(String(initialCcrsFilterValues.value || ''))
  }
  return parts.filter(Boolean).join(', ')
})

// Category filters
const {setCcrsFiltersInUrl, changePage, sortItems, filters, filter, tableOptions, resetInProgress, hasActiveFilters, setOnce} = useCategoryFilters(() => loadProducts(), categoryFilters)

const shouldShowResetFilters = computed(() => hasActiveFilters.value && userHasModifiedFilters.value)
const columnsFromFilters = computed(() => mapCcrsTableColumnsFromFilters(filters.value, selectedItemType.value))
const showLoadingSkeleton = computed(() => firstLoad.value || resetInProgress.value)

// Dropdown composable
const {
  isOpen, dropdownRef, designsSectionRef,
  handleBlur, handleDropdownFocusIn, handleDropdownFocusOut, handleDropdownScroll,
  resetScrollTracking, closeDropdown,
  handleTouchStart, handleTouchEnd, handleTouchMove
} = useCcrsDropdown({
  loadMoreProducts: () => loadMoreProducts(dropdownRef),
  loadMoreDesigns,
  itemNumbers, designs, productCount, designCount, isLoadingMore, isLoadingMoreDesigns
})

// Filter handlers
const {handleApplyFilter, handleResetFilters, handleFilterChanged} = useCcrsFilterHandlers({
  trackApplyFilter, trackResetToSearch, filter, setCcrsFiltersInUrl, loadProducts: () => loadProducts(),
  filters, defaultFilters, defaultFiltersInitialized, initialCcrsFilters,
  filtersDisabled, resetDisabled, userHasModifiedFilters, filtersAdjustedForZeroProducts, productsLoading, resetInProgress
})

// Product availability and prices
const {
  prices, availabilities, successorAvailabilities,
  loadingIndicators, loadPrices
} = useProductAvailabilityPricesHelper(products)

// Reload product and default customer
const currentProduct = ref<any>(null)
const {reloadProduct, reloadProductsInProgress} = useReloadProduct(currentProduct, i18n.locale, nuxtApp.$globalization)
const {isDefaultCustomer} = useDefaultCustomer()

// Local state
let setupComplete = false
let productDebouncer: any = null
let isClearing = false
let formData = FORM_DEFAULT_DATA.CONTACT_FORM
const filterRef = ref<any>(null)
const productTableRef = ref<any>(null)

// Event handlers
const onInputFocus = () => {
  const isMobileDevice = ('ontouchstart' in globalThis || navigator.maxTouchPoints > 0) && window.innerWidth < $md

  if (isMobileDevice) {
    setTimeout(() => {
      requestAnimationFrame(() => {
        // Scroll the search wrapper (contains label + input) into view
        const searchWrapper = document.querySelector('.ccrs__search-wrapper')
        if (searchWrapper) {
          const rect = searchWrapper.getBoundingClientRect()
          const headerOffset = (auth as any).loggedIn ? 95 : 60
          const scrollTop = window.pageYOffset + rect.top - headerOffset
          window.scrollTo({top: Math.max(0, scrollTop), behavior: 'smooth'})
        }
      })
    }, 400)
  }

  // On mobile, open overlay immediately to show it's ready for input
  // On desktop, only open if there's already content
  if (searchQuery.value.length > 0 || isMobileDevice) {
    isOpen.value = true
    if (searchQuery.value.length > 0 && !hasResultsForCurrentTerm()) {
      triggerSearch(isOpen)
    }
  }
}

const onSearchInput = () => triggerSearch(isOpen)

const handleInputEnterKey = (event: KeyboardEvent) => {
  if (!isOpen.value || isLoading.value) {return}

  const totalResults = itemNumbers.value.length + designs.value.length
  if (totalResults === 1) {
    event.preventDefault()
    const singleItem = itemNumbers.value.length === 1 ? itemNumbers.value[0] : designs.value[0]
    selectItem(singleItem)
  }
}

const handleDropdownEnterKey = (event: KeyboardEvent) => {
  // Only trigger if target is not already an item (to avoid double selection)
  const target = event.target as HTMLElement
  if (target.classList.contains('item')) {return}

  const totalResults = itemNumbers.value.length + designs.value.length
  if (totalResults === 1) {
    event.preventDefault()
    const singleItem = itemNumbers.value.length === 1 ? itemNumbers.value[0] : designs.value[0]
    selectItem(singleItem)
  }
}

const handleClearSearch = () => {
  isClearing = true
  clearSearch()
  closeDropdown()
  resetScrollTracking()

  selectedTitle.value = ''
  selectedItemType.value = 'design'
  selectedId.value = null
  userHasModifiedFilters.value = false

  const queryParams = {...route.query}
  delete queryParams.id
  delete queryParams.type
  if (filters.value && Array.isArray(filters.value)) {
    filters.value.forEach((f: any) => {
      delete queryParams[f.shortName]
      delete queryParams[`${f.shortName}_min`]
      delete queryParams[`${f.shortName}_max`]
    })
  }
  router.push({query: queryParams})

  setTimeout(() => {
    isClearing = false
    document.getElementById('ccrs-search-input')?.focus()
  }, 100)
}

const resetAllData = () => {
  handleClearSearch()
  products.value = []
  categoryFilters.value = []
  filters.value = []
  facets.value = undefined
  ccrsSearchParameters.value = null
  initialCcrsFilters.value = null
  defaultFilters.value = []
  prices.value = []
  availabilities.value = []
  successorAvailabilities.value = []
  loadingIndicators.value.prices = []
  loadingIndicators.value.availabilities = []
  loadingIndicators.value.successorAvailabilities = []
  selectedCompetitorId.value = null
  resultsExpanded.value = false
  isContactPopupOpen.value = false
  filtersDisabled.value = true
  resetDisabled.value = true
  productsLoading.value = false
  firstLoad.value = true
  setOnce.value = false
  filtersAdjustedForZeroProducts.value = false
}

const selectItem = async (item: Record<string, any>) => {
  trackResultClick(item, searchQuery.value)
  closeDropdown()

  // Show skeleton immediately, same as on first load
  firstLoad.value = true
  setOnce.value = true // Prevent filters watcher from interfering

  selectedTitle.value = item?.type === 'design' ? i18n.tv('vendorDesign', 'ccrs') : i18n.tv('vendorItem', 'ccrs')
  selectedItemType.value = item?.type === 'product' ? 'product' : 'design'
  selectedId.value = item.id

  filtersDisabled.value = true
  products.value = []
  categoryFilters.value = []
  filters.value = []
  facets.value = undefined
  ccrsSearchParameters.value = null
  initialCcrsFilters.value = null
  defaultFilters.value = []
  prices.value = []
  availabilities.value = []
  successorAvailabilities.value = []
  loadingIndicators.value.prices = []
  loadingIndicators.value.availabilities = []
  loadingIndicators.value.successorAvailabilities = []

  await router.push({query: {...route.query, id: selectedId.value, type: selectedItemType.value}})

  const select = getSelectByScope(PROPERTY_SCOPE_NAMES.CATEGORY_PAGE_NEW)?.join(',')
  const payload: DetailsRequestBody = {
    designId: item.type === 'design' ? item.id : null,
    productId: item.type === 'product' ? item.id : null,
    region: nuxtApp.$globalization.getRegion(),
    languageCode: i18n.locale.value,
    debug: false,
    select,
    orderBy: 'productRank asc,skuNumeric asc',
    competitorId: item.competitorId ?? null
  }

  const ALLOWED_ZERO_PRODUCT_FILTERS = ['brand', 'productDesign', 'material1Name', 'material1ShoreHardness', 'material1Compound', 'hasInchClass']

  const det = await postDetails(payload).catch(() => null)
  // Use local var — products.value stays [] so skeletons remain visible until loadProducts() completes
  const detProducts = det?.products ?? []
  categoryFilters.value = det?.filters.map((f: ApiFilter) => mapCategoryFilter(f, true, true)) ?? []

  if (det?.ccrsSearchParameters) {
    ccrsSearchParameters.value = det.ccrsSearchParameters
  }

  // eslint-disable-next-line eqeqeq
  const filtersWithValues = cleanupFilters(categoryFilters.value.filter(f => f.Value != null), true)
  filtersWithValues.forEach((f: any) => {f.isActive = true})
  await setCcrsFiltersInUrl(filtersWithValues)
  filters.value = cleanupFilters(categoryFilters.value, true)

  await nextTick()
  initialCcrsFilters.value = filtersWithValues ? structuredClone(filtersWithValues) : []
  facets.value = det?.facets
  setupComplete = true

  filtersDisabled.value = true
  if (!detProducts.length) {
    filters.value.forEach((f: any) => {
      if (!ALLOWED_ZERO_PRODUCT_FILTERS.includes(f.indexPath)) {
        f.value = null
        f.values = []
      }
    })
    filtersAdjustedForZeroProducts.value = true
    await setCcrsFiltersInUrl(filters.value)
  } else {
    filtersAdjustedForZeroProducts.value = false
  }
  resetDisabled.value = !filtersAdjustedForZeroProducts.value
  userHasModifiedFilters.value = filtersAdjustedForZeroProducts.value

  scrollDownOnSearch()
  await loadProducts()
  await nextTick()

  setTimeout(() => {
    defaultFilters.value = filters.value?.map((f: any) => ({...f})) ?? []
    defaultFiltersInitialized.value = true
  }, 300)
}

const scrollDownOnSearch = () => {
  const el = document.querySelector('.ccrs__results-wrapper')
  if (el) {
    window.scrollTo({top: el.getBoundingClientRect().top + window.pageYOffset - 111, behavior: 'smooth'})
  }
}

const openContactDetails = (addInfos: boolean) => {
  if (!(auth as any).loggedIn) {
    (nuxtApp.$eventbus as any).emit('showContactPopup', {
      simpleMode: true,
      topicId: '1e53b8d6c40045a1b3eca1d70eb29350',
      prefilledMessage: generatePrefilledMessage(false, addInfos),
      trackingCallback: trackContactSend
    })
    return
  }

  contactAdditionalInfo.value = generatePrefilledMessage(true, addInfos)
  isContactPopupOpen.value = true
  trackShowContactDetails({searchTerm: searchQuery.value, additionalInfo: (contactAdditionalInfo.value as string).trim()})
}

const generatePrefilledMessage = (onlyAdditionalInfo: boolean, addInfos: boolean) => {
  const defaultMessage = `${i18n.tv('searchQueryLabel', 'ccrs')}: ${searchQuery.value}\n`
  if (!searchQuery.value) {
    return ''
  }
  const hasSelectedItem = selectedId.value !== null
  if (addInfos && hasSelectedItem) {
    const itemInfo = `\n${selectedTitle.value}:  ${displayInfo.value}\n${onlyAdditionalInfo ? '' : '--------\n'}`
    return onlyAdditionalInfo ? `${itemInfo}` : `${defaultMessage}${itemInfo}`
  }
  return onlyAdditionalInfo ? '' : `${defaultMessage}--------\n`
}

const closeContactPopup = () => {
  isContactPopupOpen.value = false
}

const loadProducts = async () => {
  if (!setupComplete || isClearing) {return}

  if (productDebouncer) {clearTimeout(productDebouncer)}

  productDebouncer = setTimeout(async () => {
    if (!process.client) {return}
    productsLoading.value = true

    let productFacets: string[] | null = null
    if (Array.isArray(categoryFilters.value)) {
      productFacets = categoryFilters.value
        .filter(cat => cat.Attribute.IndexPath && cat.Attribute.IsFilterable &&
          FACET_VALUE_TYPES.includes(cat.Attribute.ValueType) &&
          cat.Attribute.TechnicalName.toLowerCase() !== NON_ATTRIBUTE_FILTERS.CATALOG_FLAG.toLowerCase())
        .map(cat => cat.Attribute.IndexPath)
      productFacets = cleanupFacets(productFacets)
    }

    if (filters.value?.length > 0 && Array.isArray(categoryFilters.value)) {
      const categoryAttributeSelects = categoryFilters.value?.map(f => f.IndexPath) ?? []
      const customFilters = showInchValues.value === true ? [SHOW_INCH_VALUES_FILTER.customQuery] : []
      const requestFilters = filters.value.filter(f => !(f.indexPath === 'hasInchClass' && f.values === false))
      products.value = await useProductService().getProductsForCcrs(
        productFacets,
        nuxtApp.$globalization.getRegion(),
        i18n.locale.value,
        [...requestFilters],
        tableOptions.value,
        customFilters,
        PROPERTY_SCOPE_NAMES.CATEGORY_PAGE_NEW,
        categoryAttributeSelects
      )
      facets.value = products.value?.facets
      firstLoad.value = false
    }

    productsLoading.value = false
    if (resetInProgress.value) {resetInProgress.value = false}
  }, 250)
}

const addItemToBasket = (_e: any, productLocal: any) => {
  trackAddToCart(buildDynamicProductProps(productLocal, productPropsKeys))
  currentProduct.value = getMappedProductNew(productLocal)
  openCartModal.value = true
}

const onProductSuccessfullyAdded = () => {openCartModal.value = false}
const closeCartModal = () => {openCartModal.value = false}
const changePageLocal = (event: any) => changePage(event)
const clearFormData = () => {formData = FORM_DEFAULT_DATA.CONTACT_FORM}

const handleCalculate = async () => {
  await Promise.all([loadPrices()])
}

/**
 * Opens the competitor ComboDropDown by focusing the input and
 * dispatching Alt+ArrowDown keyboard event (standard dropdown open shortcut)
 */
const openComboDropdown = () => {
  if (!competitorCombo.value) {return}

  const input = competitorCombo.value.$el?.querySelector('input.k-input-inner') as HTMLInputElement
  if (!input) {return}

  input.focus()

  setTimeout(() => {
    input.dispatchEvent(new KeyboardEvent('keydown', {
      key: 'ArrowDown',
      code: 'ArrowDown',
      keyCode: 40,
      which: 40,
      altKey: true,
      bubbles: true,
      cancelable: true
    }))
  }, 50)
}

// Watchers
watch(selectedCompetitorId, (newVal, oldVal) => {
  if (newVal !== null && oldVal !== undefined) {
    trackVendorSelection(newVal.name, newVal.id, searchQuery.value)
  }
  if (newVal?.id !== oldVal?.id && searchQuery.value !== '') {
    resetPagination()
    isOpen.value = true
    performSearch(searchQuery.value)
  }
  if (oldVal !== null && newVal === null) {
    setTimeout(openComboDropdown, 100)
  }
})

watch(() => route.query, (newQuery) => {
  if (newQuery.order_by === undefined && newQuery.id === undefined && newQuery.type === undefined) {
    resetAllData()
  }
}, {deep: true})

watch(loadedCustomerUuid, resetAllData)

// Lifecycle
onMounted(async () => {
  loadingDone.value = true
  const input = competitorCombo.value?.$el?.querySelector('input')
  input?.addEventListener('focus', openComboDropdown)

  const comps = await getCompetitors().catch(() => null)
  if (Array.isArray(comps)) {
    topCompetitorOptions.value = topCompetitorOptions.value.filter(top => comps.some((c: any) => c.name === top.name))
    const cOptionsFiltered = comps.map(c => ({id: c.competitorId, name: c.name}))
      .filter(c => topCompetitorOptions.value.every(top => top.name !== c.name))

    competitorOptions.value = [...topCompetitorOptions.value, ...cOptionsFiltered]
  }

  const {id: selectedIdParam, type: selectedItemTypeParam} = route.query
  if (selectedIdParam && selectedItemTypeParam) {
    await selectItem({id: selectedIdParam, type: selectedItemTypeParam})
  }
})
</script>

<style lang="scss">
.ccrs-root {
[id='CCRSPage'] {
  padding: 0 1.5rem 1rem;
  background-color: $color__fst__white;

  .ccrs {
    &__main-controls {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;

      &__vendor {
        width: 14.3125rem;

        &__autocomplete {
          .k-input-button { display: none; }

          .k-input-solid {
            border: 0.0625rem solid $color--input__border;
            border-radius: 0;
            box-sizing: border-box;
          }
          .k-input-inner { padding: 0.75rem 1rem; }

          .k-input-solid:focus,
          .k-input-solid.k-focus,
          .k-input-solid.k-state-focused {
            border: 0.0625rem solid $color--input__border;
            box-shadow: none;
            outline: none;
          }
        }

        &__autocomplete--with-clear .k-clear-value { display: inline-flex; }

        .ccrs__main-controls__vendor__autocomplete.ccrs__autocomplete--with-clear .k-clear-value {
          display: inline-flex;
          color: $color__black;
          opacity: 1;

          .k-svg-icon {
            width: 1.5rem;
            height: 1.5rem;
            margin-right: .5rem;
            margin-left: .3rem;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23000000' d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'/%3E%3C/svg%3E");
            background-size: contain;
            background-repeat: no-repeat;
            background-position: center;
            svg { display: none; }
          }
        }
      }
    }

    &__title {
      margin-bottom: 2rem;
      &__h1 { margin: 0 0 1.25rem; }
      &__h2 { margin-top: 2rem; }
    }

    &__search-wrapper {
      position: relative;
      width: 100%;
      max-width: 32.5rem;
      flex: 1;
    }

    &__search-label {
      display: block;
      margin-bottom: 0.5rem;
      font-size: 0.875rem;
      font-weight: 300;
    }

    &__search-input {
      width: 100%;
      padding: 0.75rem 3rem 0.75rem 1rem;
      border: 0.0625rem solid $color--input__border;
      border-radius: 0;
      box-sizing: border-box;
    }

    &__search-clear {
      position: absolute;
      top: 2.125rem;
      right: 0.5rem;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        font-size: 1.5rem;
        color: $color__black;
      }

      // Mobile optimization: ensure X button aligns with input field
      @include breakpoint-down($md) {
        top: auto;
        bottom: 0.75rem;
      }
    }

    &__dropdown {
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      max-width: 32.5rem;
      max-height: 30rem;
      background: $color__white;
      box-shadow: $shadow-default;
      overflow-y: auto;
      z-index: 10;
      box-sizing: border-box;

      // Mobile optimization: reduce max-height to account for on-screen keyboard
      @include breakpoint-down($md) {
        max-height: 40vh;
      }

      &::-webkit-scrollbar-thumb {
        background-color: $color__fst__primary;
        border-radius: 0.375rem;
        border: 0.1875rem solid $color__fst__gray__075;
      }

      &::-webkit-scrollbar-thumb:hover {
        background-color: darken($color__fst__primary, 8%);
      }

      scrollbar-width: auto;
      scrollbar-color: $color__fst__primary $color__fst__gray__020;

      &--loading .item--skeleton {
        pointer-events: none;
      }
    }

    &__no-results {
      padding: 1rem;
      text-align: left;
      font-weight: $base-text-font-weight-light;
      font-size: 0.875rem;
      line-height: 1.5;

      div {
        margin-bottom: 0.25rem;
      }
    }

    &__open-contact {
      display: block;
      font-weight: $base-text-font-weight-bold;
      text-decoration: none;
      color: $color__secondary;
      cursor: pointer;

      &:hover {
        text-decoration: none;
      }
    }
  }

  .dropdown__info-header {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    font-weight: $base-text-font-weight-light;

    b,
    strong {
      font-weight: $base-text-font-weight-semi-bold;
    }
  }

  .dropdown__section {
    &:not(:last-child) {
      border-bottom: 0.0625rem solid $color--input__border;
    }

    &:first-child {
      .section__header {
        border-bottom: none;
      }
    }

    &--opened:first-child .section__header {
      border-bottom: 0.0625rem solid $color--input__border;
    }

    &--designs .section__header {
      border-top: 0.125rem solid $color--input__border;
      padding-top: 1rem;
    }

    &--opened {
      .section__header .navigation_expand_more.icon--svg {
        transform: rotate(180deg);
      }

      .section__items {
        display: flex;
        flex-direction: column;
        opacity: 1;
      }
    }
  }

  .section__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    border-top: 0.0625rem solid $color--input__border;
    border-bottom: 0.0625rem solid $color--input__border;
    font-size: .875rem;
    font-weight: 600;
    color: $color__primary;
    cursor: pointer;

    .navigation_expand_more {
      font-size: 1.5rem;
    }

    .navigation_expand_more.icon.icon--svg {
      transition: all .35s ease-in-out;
    }
  }

  .section__items {
    display: none;
    opacity: 0;
    transition: opacity .3s ease-in-out;
  }

  .item {
    padding: .5rem 1rem;
    border-bottom: 0.0625rem solid $color--input__border;
    cursor: pointer;
    font-weight: $base-text-font-weight-light;
    transition: background-color 0.15s ease-in-out;

    &:hover {
      background-color: $color__fst__blue__010;
    }

    &:last-child {
      border-bottom: none;
    }

    &__line {
      font-size: 0.875rem;
    }

    &__list {
      margin: .25rem 0 0;
      padding-left: 1.5rem;
      list-style-type: disc;

      .item__line {
        font-size: 0.75rem;
        margin: 0;
        padding-left: 0.25rem;

        &::marker {
          font-size: 0.625rem;
        }
      }
    }

    &__header {
      display: flex;
      gap: 0.5rem;
      margin-bottom: 0.5rem;
    }

    &__vendor {
      font-weight: $base-text-font-weight-light;
    }

    &__label {
      margin-right: 0.5rem;
      display: inline-block;
    }

    &__title {
      font-weight: $base-text-font-weight-light;
      margin-bottom: 0.5rem;
    }

    &__description {
      font-size: .75rem;
    }

    &__measurements {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      margin: 0.5rem 0;
    }

    &__category {
      display: inline-block;
    }

    &__vendor em, &__value em, &__title em, &__category em {
      font-weight: $base-text-font-weight-bold;
      font-style: normal;
    }
  }

  .measurement {
    padding: 0.25rem 0.5rem;
    border-radius: 0;

    strong {
      font-weight: $base-text-font-weight-semi-bold;
    }
  }

  .ccrs__results__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    flex-wrap: wrap;
    gap: 1rem;

    &-right {
        display: flex;
        gap: 1.5rem;
        align-items: center;
    }

    .ccrs__results__sort, .ccrs__results__entries {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        label {
            font-size: 0.875rem;
            white-space: nowrap;
        }

        .k-dropdown {
            width: auto;
            min-width: 8.75rem;
        }
    }
  }

  /* Fix for shadows and padding */
  .ccrs__results__filters {
      overflow: visible;
      padding-bottom: 2rem; /* Adapt padding below result table */
  }

  /* Footer alignment */
  .html-message__content {
      align-items: flex-start !important; /* Force left alignment */
      text-align: left;
  }
}

.ccrs__results-wrapper {
  background-color: $color__fst__blue__010;

  .ccrs__zero-products-message {
    margin-bottom: 3rem;
  }

  &__results {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: 1rem 1.5rem;
    margin-top: 1rem;

    .category-filter-container {
      padding: 0;
      margin-bottom: 0;
      box-sizing: border-box;
      width: 100%;

      @include breakpoint-down($md) {
        padding: 1.5rem;
        background-color: $color__white;
        box-shadow: $shadow-default;
      }

      .category-filter-header-wrapper {
        margin-bottom: 0;
        padding: 0 0 .5rem;

        .category-filter__unit-switch {
          margin-bottom: .5rem;
        }

        @include breakpoint-down($md) {
           padding: 0;
        }
      }

      .category-filter-container__button-container {
        display: flex;
        justify-content: flex-end;
        gap: 1rem;
        margin-top: 1.5rem;

        .button--apply-filters {
          margin-left: 0;
        }

        .k-button {
          margin-bottom: 0;
        }

        .button__secondary--showMore {
            display: flex !important;
        }

        @include breakpoint-down($md) {
           flex-direction: column;
           width: 100%;
           gap: 1rem;

           .k-button {
              margin-bottom: 0;
              width: 100%;
              justify-content: center;
           }

           .button__secondary--showMore {
              display: none !important;
            }
        }
      }

      @include breakpoint-down($md) {
        .category-filter-container__filters__wrapper {
          margin: 0 !important;
          width: 100% !important;

          &__content {
            &__filter {
              flex-basis: 100% !important;
              width: 100% !important;
              max-width: 100% !important;
            }

            .filter-item-wrapper {
                width: 100% !important;
                margin-left: 0 !important;
            }

            &__filter .toggleSwitch {
                padding: 0 !important;
            }
          }
        }

        .category-filter__unit-switch {
          padding-top: .5rem;
        }
      }

      .category-filter-header {
        font-size: 1.25rem;
        margin: 0;
      }

      &:not(.collapsed-mobile) .category-filter-header {
        margin-bottom: .5rem;
      }

      .filter-item-wrapper {
        @include breakpoint-down($md) {
           margin: 1.25rem 0 0;
        }
      }
    }

    h2 {
      margin: 0;
    }

    .ccrs__results__filters {
      .product-table-content.content {
        padding: 0;
        margin-top: 2rem;
      }

      .product-table-content > .quantity-controls {
        right: -1rem;
      }

      .ccrs__zero-products-message .html-message__content {
        flex-flow: row wrap;
        gap: 0.25rem;
      }

      .html-message__content {
        display: flex;
        flex-direction: column;
      }

      .warningcontainer--item.info-message {
        background-color: $color__fst__blue__010;
      }

      .skeleton__table {
        margin-left: 0;
        margin-right: 0;
      }

      @include breakpoint-down($md) {
        .inch-value--filter {
          display: none !important;
        }

        .product-table-content .grid {
          min-height: 0 !important;
          padding-bottom: 0 !important;
        }

        .k-pager {
          border-top: none !important;
          padding-top: 1rem;
        }
      }

      @include breakpoint-down(26.875rem) {
        .pagination-controls.experimental-position {
          width: calc(100% - .2rem)
        }

        .pagination-controls .pagination-size-dropdown {
          bottom: 2.25rem;
          width: 100%;
        }

        .k-pager {
          margin-top: 2.595rem;
          top: $default-margin-padding-width;
          margin-bottom: 0.75rem;
          justify-content: flex-start;
        }
      }
    }
  }
}

&:has(.product-table-content),
&:has(.product-table__no-products-found),
&:has(.ccrs__results-wrapper__results) {
  background-color: $color__fst__blue__010;
}
}

@include breakpoint-down($lg) {
  [id='CCRSPage'] .ccrs {
    &__main-controls__vendor {
      width: 100%;
      min-width: 100%;
      max-width: 32.5rem;
    }

    &__search-wrapper {
      min-width: 100%;
      flex: 1 1 100%;
    }
  }
}

@include breakpoint-down($md) {
  .ccrs {
    &__title {
      &__h1 {
        padding-top: 1rem;
      }
    }

    &__search-clear {
      padding-top: 1rem;
    }

    &__search-input:focus,
    &__search-input:focus-visible,
    &__search-input:focus-within {
      outline: .125rem solid $color__black !important;
    }
  }

  [id='CCRSPage'] .ccrs__main-controls__vendor .ccrs__main-controls__vendor__autocomplete.ccrs__autocomplete--with-clear .k-clear-value {
    width: 2rem;
  }

  .ccrs__results-wrapper {
    &__results {
      padding: 1rem !important;
      margin: 0 !important;
      gap: 1.25rem !important;

      .category-filter-container .filter-item-wrapper {
        margin: 1rem 2rem 2rem;
      }

      .ccrs__results__filters {
        .warningcontainer--item.info-message {
          margin-top: 1rem;

          .alert-icon {
            flex-shrink: 0;
            width: 1.5rem;
            height: 1.5rem;
          }

          svg {
            width: 1.5rem;
            height: 1.5rem;
          }
        }

        .product-table-content.content {
          padding: 0;
        }
      }
    }

    .category-filter-container.pure-form {
      padding: 1rem;
    }

    .ccrs__results-wrapper__results .ccrs__results__filters .product-table-content.content {
      padding: 1rem;
    }
  }
}
</style>
